import { getGroups, setGroups } from "./state";
import { getSheet } from "../../sheets";
import {
	isInactiveSheet,
	getSheetGroups,
	setSheetGroups,
	refreshGroupView,
	isGroupingAllowed,
} from "./helpers";

export function addGroup(view, type, id, page) {
	if (!isGroupingAllowed(view)) return;
	const range = _resolveGroupRange(view, type, id, page);
	if (!range) return;
	_applyGroupDelta(view, type, range[0], range[1], 1, page);
}

export function removeGroup(view, type, id, page) {
	if (!isGroupingAllowed(view)) return;
	const range = _resolveGroupRange(view, type, id, page);
	if (!range) return;
	_applyGroupDelta(view, type, range[0], range[1], -1, page);
}

export function updateGroupPositions(view, name, inc, data, start) {
	if (name !== "column" && name !== "row") return;

	const groups = getGroups(view, name);
	if (!groups.length) return;

	_shiftGroups(groups, start[name], inc);
	if (inc < 0) mergeAdjacentGroups(groups);
	setGroups(view, name, groups);

	if (data && data.table && data.table.groups)
		data.table.groups[name] = webix.copy(groups);

	refreshGroupView(view, name);
}

export function mergeAdjacentGroups(groups, preSorted = false) {
	if (!preSorted)
		groups.sort((a, b) => a.level - b.level || a.start - b.start);

	// merge adjacent groups at same level
	for (let i = groups.length - 1; i > 0; i--) {
		const curr = groups[i];
		const prev = groups[i - 1];

		if (curr.level === prev.level && prev.end + 1 === curr.start) {
			prev.end = curr.end;
			prev.open = prev.open || curr.open;
			groups.splice(i, 1);
		}
	}
}

function _resolveGroupRange(view, type, indexes, page) {
	if (!indexes) {
		if (isInactiveSheet(view, page)) return null;
		const sel = view.$$("cells").getSelectArea();
		if (!sel) return null;
		return [sel.start[type], sel.end[type]];
	}
	if (webix.isArray(indexes))
		return [
			Math.min(indexes[0], indexes[1]),
			Math.max(indexes[0], indexes[1]),
		];
	return [indexes, indexes];
}

function _applyGroupDelta(view, type, start, end, delta, page) {
	const eventId = delta > 0 ? "group" : "ungroup";
	const ev = type === "row" ? "onRowOperation" : "onColumnOperation";

	if (isInactiveSheet(view, page)) {
		const sheet = getSheet(view, page);
		if (!sheet) return;
		const prev = webix.copy(getSheetGroups(sheet, type));
		const res = _rebuildGroupsWithDelta(prev, start, end, delta);
		if (!res.changed || (delta > 0 && res.overflow)) return;
		setSheetGroups(sheet, type, res.groups);
		view.callEvent(ev, [
			eventId,
			start,
			end,
			page,
			{ value: prev, newValue: webix.copy(res.groups), page },
		]);
		return;
	}

	const groups = getGroups(view, type);
	const prev = webix.copy(groups);
	const res = _rebuildGroupsWithDelta(groups, start, end, delta);
	if (!res.changed || (delta > 0 && res.overflow)) return;
	setGroups(view, type, res.groups);
	view.callEvent(ev, [
		eventId,
		start,
		end,
		view.getActiveSheet(),
		{ value: prev, newValue: webix.copy(res.groups) },
	]);
	refreshGroupView(view, type);
	_restoreFocus(view);
}

function _shiftGroups(groups, start, inc) {
	for (let i = groups.length - 1; i >= 0; i--) {
		const g = groups[i];
		if (inc > 0) {
			// inserting
			if (g.start >= start) g.start += inc;
			if (g.end >= start) g.end += inc;
		} else {
			// deleting
			const delEnd = start - inc - 1;
			if (g.start >= start && g.end <= delEnd) {
				groups.splice(i, 1);
				continue;
			}
			if (g.start >= start) g.start = Math.max(start, g.start + inc);
			if (g.end >= start) g.end = Math.max(start - 1, g.end + inc);
			if (g.start > g.end) {
				groups.splice(i, 1);
			}
		}
	}
}

function _restoreFocus(view) {
	const grid = view.$$("cells");
	if (grid && grid.getSelectArea()) {
		if (webix.UIManager.getFocus() !== grid) webix.UIManager.setFocus(grid);
	}
}

// depth-profile algorithm - builds a new groups array from an incremental add/remove delta

function _rebuildGroupsWithDelta(groups, start, end, delta) {
	const profile = _buildDepthProfile(groups, start, end);
	let changed = false;

	for (let pos = start; pos <= end; pos++) {
		const ind = pos - profile.offset;
		const current = profile.depths[ind] || 0;
		const next = delta > 0 ? current + 1 : Math.max(0, current - 1);

		if (next !== current) {
			profile.depths[ind] = next;
			changed = true;
		}
	}

	if (!changed) return { changed: false, overflow: false };

	if (_getMaxDepth(profile.depths) > 8)
		return { changed: false, overflow: true };

	return {
		changed: true,
		overflow: false,
		groups: _buildGroupsFromProfile(profile, groups, delta),
	};
}

function _buildDepthProfile(groups, start, end) {
	let min = start,
		max = end;
	for (let i = 0; i < groups.length; i++) {
		if (groups[i].start < min) min = groups[i].start;
		if (groups[i].end > max) max = groups[i].end;
	}

	const diffs = new Array(max - min + 2).fill(0); // +2 for the sentinel at end+1
	for (let i = 0; i < groups.length; i++) {
		diffs[groups[i].start - min]++;
		diffs[groups[i].end - min + 1]--;
	}

	const depths = new Array(max - min + 1).fill(0);
	let running = 0;
	for (let i = 0; i < depths.length; i++) {
		running += diffs[i];
		depths[i] = running;
	}

	return { offset: min, depths };
}

function _buildGroupsFromProfile(profile, prevGroups, delta) {
	const prevLookup = _createGroupLookup(prevGroups);
	const groups = [];
	const maxDepth = _getMaxDepth(profile.depths);

	for (let depth = 1; depth <= maxDepth; depth++) {
		let runStart = -1;

		for (let i = 0; i <= profile.depths.length; i++) {
			const active =
				i < profile.depths.length && (profile.depths[i] || 0) >= depth;

			if (active && runStart === -1) {
				runStart = i;
				continue;
			}

			if (!active && runStart !== -1) {
				const start = profile.offset + runStart;
				const end = profile.offset + i - 1;
				const level = depth - 1;
				const prev =
					prevLookup[_makeGroupKey(start, end, level)] ||
					prevLookup[_makeGroupKey(start, end, level - delta)];

				groups.push({
					id: prev ? prev.id : webix.uid(),
					start,
					end,
					level,
					open: prev ? prev.open : true,
				});

				runStart = -1;
			}
		}
	}

	return groups;
}

function _createGroupLookup(groups) {
	const map = {};
	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];
		map[_makeGroupKey(g.start, g.end, g.level)] = g;
	}
	return map;
}

function _makeGroupKey(start, end, level) {
	return `${level}:${start}:${end}`;
}

function _getMaxDepth(depths) {
	let max = 0;
	for (let i = 0; i < depths.length; i++) {
		if (depths[i] > max) max = depths[i];
	}
	return max;
}
